import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { ProductAction, VariantPickerModel } from 'autogen/swagger/all';
import { AddToBasket } from 'autogen/translation-keys/trans-website-basket';
import { SelectButton } from 'autogen/translation-keys/trans-website-productvariantpicker';
import {
    BackInStockNotification,
    BuyClickAndCollect,
    BuyNow,
    FindInStore
} from 'autogen/translation-keys/trans-website-shared-buttons';
import { Button, ButtonWithSpinner } from 'components/Button';
import LinkActionButton from 'components/Button/LinkActionButton';
import { useAddProduct } from 'components/generic/productCTA/components/AddProduct/action';
import { useOpenClickAndCollectCallback } from 'components/generic/productCTA/components/OpenClickAndCollect/hooks/useOpenClickAndCollect';
import { useOpenFindInStoreCallback } from 'components/generic/productCTA/components/OpenFindInStore/FindInStoreButton';
import { useOpenStockNotificationCallback } from 'components/generic/productCTA/components/OpenStockNotification/StockNotificationButton';
import ProductActionRenderer from 'components/generic/productCTA/ProductActionRenderer';
import type { IProductActionButton, ProductActionProps } from 'components/generic/productCTA/shared/types';
import usePowerstep from 'components/powerstep/shared/hooks/usePowerstep';
import useRegimeQuickView from 'components/product/regime/components/RegimeQuickView/components/RegimeQuickViewButton/hooks/useRegimeQuickView';
import { useSlides } from 'components/quickView';
import ActionFooter from 'components/quickView/components/ActionFooter';
import toast from 'components/Toast';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import ActiveSectionContext from '../shared/context/activeSection';
import TrackingParamsContext from '../shared/context/TrackingParams';
import useCloseVariantPickerQuickview from '../shared/hooks/useCloseVariantPickerQuickview';

const StyledActionFooter = styled(ActionFooter)`
    flex: 0 0 auto;
`;

type CTAButtonProps = {
    action: ProductAction;
    productId: VariantPickerModel['productId'];
    externalProductId: VariantPickerModel['externalProductId'];
    linkAction: VariantPickerModel['linkAction'];
};

function CustomBuyNow({ params, trackingInfo, children, ...restProps }: ProductActionProps['BuyNow']) {
    const { t } = useTranslation();
    const [openPowerstep] = usePowerstep();
    const AddProduct = useAddProduct();
    const trackingParams = useContext(TrackingParamsContext);
    const { powerstepEnabled } = useFeatureToggles();
    const [{ isOpen: regimeQuickViewIsOpen }] = useRegimeQuickView();
    const { pop } = useSlides();
    const closeVariantPickerQuickview = useCloseVariantPickerQuickview();

    async function buttonClick() {
        const response = await AddProduct({ params, trackingInfo }, trackingParams ? trackingParams : undefined);
        if (params.showPowerstepOnAddToBasket && powerstepEnabled) {
            openPowerstep([params.productId]);
        } else {
            toast.success<string>(t(AddToBasket), {
                position: 'top-right'
            });
        }

        if (response?.success && regimeQuickViewIsOpen) {
            setTimeout(() => {
                pop();
            }, 1000);
        }

        closeVariantPickerQuickview();
    }

    return (
        <ButtonWithSpinner onClick={buttonClick} type="button" {...restProps}>
            {children || t(BuyNow)}
        </ButtonWithSpinner>
    );
}

function CustomBackInStockNotification({
    params,
    trackingInfo,
    children,
    ...restProps
}: ProductActionProps['BackInStockNotification']) {
    const { t } = useTranslation();
    const { productId } = params;
    const onClickHandler = useOpenStockNotificationCallback({ productId, trackingInfo });
    const closeVariantPickerQuickview = useCloseVariantPickerQuickview();

    return (
        <ButtonWithSpinner
            onClick={async () => {
                await onClickHandler();
                closeVariantPickerQuickview();
            }}
            variant="primary"
            type="button"
            size="lg"
            {...restProps}
        >
            {children || t(BackInStockNotification)}
        </ButtonWithSpinner>
    );
}

function CustomFindInStore({ params, trackingInfo, children, ...restProps }: ProductActionProps['FindInStore']) {
    const { t } = useTranslation();
    const { productId } = params;
    const onClickHandler = useOpenFindInStoreCallback({ productId, trackingInfo });
    const closeVariantPickerQuickview = useCloseVariantPickerQuickview();

    return (
        <ButtonWithSpinner
            onClick={async () => {
                await onClickHandler();
                closeVariantPickerQuickview();
            }}
            variant="primary"
            type="button"
            size="lg"
            {...restProps}
        >
            {children || t(FindInStore)}
        </ButtonWithSpinner>
    );
}

function CustomClickAndCollect({
    params,
    trackingInfo,
    children,
    ...restProps
}: ProductActionProps['ClickAndCollect']) {
    const { t } = useTranslation();
    const { productId } = params;
    const onClickHandler = useOpenClickAndCollectCallback({ productId, trackingInfo });
    const closeVariantPickerQuickview = useCloseVariantPickerQuickview();

    return (
        <ButtonWithSpinner
            onClick={async () => {
                await onClickHandler();
                closeVariantPickerQuickview();
            }}
            variant="primary"
            type="button"
            size="lg"
            {...restProps}
        >
            {children || t(BuyClickAndCollect)}
        </ButtonWithSpinner>
    );
}

function CTAButton({ action, productId, externalProductId, linkAction }: CTAButtonProps) {
    const [{ isOpen: quickViewIsOpen }] = useRegimeQuickView();
    const productActionButtonModel: IProductActionButton['model'] = {
        action,
        trackingContext: { eventCategory: 'variantPicker', type: 'Page' },
        productId,
        externalProductId,
        productNavigationLink: linkAction,
        isSubscriptionContext: undefined,
        isSubscribable: undefined,
        minimumSalesQuantity: undefined,
        bundleInfo: undefined,
        listName: undefined,
        fromPowerstep: false,
        addToBasketType: 'Default',
        requireLoginForCheckout: undefined,
        recommendationPlacement: undefined,
        variants: undefined,
        showPowerstepOnAddToBasket: !quickViewIsOpen
    };

    return (
        <ProductActionRenderer
            size="lg"
            model={productActionButtonModel}
            overriddenActionComponents={{
                BuyNow: CustomBuyNow,
                BackInStockNotification: CustomBackInStockNotification,
                FindInStore: CustomFindInStore,
                ClickAndCollect: CustomClickAndCollect
            }}
        />
    );
}

type FooterProps = {
    action: VariantPickerModel['action'];
    productId: VariantPickerModel['productId'];
    linkAction: VariantPickerModel['linkAction'];
    hasMoreChoices: boolean;
    isPDPRequest?: boolean;
};

export default function Footer({ action, productId, linkAction, isPDPRequest, hasMoreChoices }: FooterProps) {
    const { activeSectionIndex, totalSectionAmount, setActiveSectionIndex } = useContext(ActiveSectionContext);
    const showSelectButton =
        hasMoreChoices &&
        totalSectionAmount !== null &&
        totalSectionAmount > 1 &&
        activeSectionIndex + 1 < totalSectionAmount;
    const { t } = useTranslation();

    if (isPDPRequest && action === 'BuyNow') {
        return (
            <StyledActionFooter size={{ xs: 'md', xl: 'lg' }}>
                <LinkActionButton action={linkAction} size="lg">
                    {t(SelectButton)}
                </LinkActionButton>
            </StyledActionFooter>
        );
    }

    return (
        <StyledActionFooter size={{ xs: 'md', xl: 'lg' }}>
            {showSelectButton ? (
                <Button
                    onClick={() => {
                        setActiveSectionIndex(activeSectionIndex + 1);
                    }}
                    size="lg"
                >
                    {t(SelectButton)}
                </Button>
            ) : (
                <CTAButton
                    action={action}
                    productId={productId}
                    externalProductId={productId}
                    linkAction={linkAction}
                />
            )}
        </StyledActionFooter>
    );
}
